import React, { useMemo } from 'react';
import styled from "styled-components";
import { HospitalityActionState, HospitalityMeetingBookingSummary } from "@features/meeting-rooms/pages/models/hospitality.models";
import Colours from "@shared/ui/atoms/Colours";
import { Column } from "@shared/ui/atoms/StructuralLayout";
import { BodyLarge, BodySmall } from "@shared/ui/atoms/fonts/Body";
import moment from "moment";
import { UserAvatarAndName } from "@shared/ui/molecules/UserAvatarAndName";
import { useTranslation } from "react-i18next";
import { HospitalityOrderProgressStateControls } from "./HospitalityOrderProgressStateControls";
import { tablet } from "@shared/ui/atoms/MediaQueries";

export function HospitalityBookingSummary(props: Props) {
  const {summary} = props;
  const {t} = useTranslation();

  const dateToFromString = useMemo(() => {
    const from = moment(summary.fromDateTime);
    const to = moment(summary.toDateTime);
    return `${from.format('dddd Do MMMM')} - ${from.format('HH:mm')} - ${to.format('HH:mm')}`;
  }, [summary.fromDateTime, summary.toDateTime]);

  const totalQuantity = useMemo(() => {
    const oi = summary.orderedItem.map((i => i.quantity));
    const si = summary.specialOrderedItem.map((i => i.quantity));
    return [...oi, ...si].reduce((sum, cur) => sum + cur, 0);
  }, [summary.orderedItem, summary.specialOrderedItem]);

  const totalCost = useMemo(() => {
    const oi = summary.orderedItem.map((i => i.totalCost));
    const si = summary.specialOrderedItem.map((i => i.totalCost));
    return [...oi, ...si].reduce((sum, cur) => sum + cur, 0).toFixed(2);
  }, [summary.orderedItem, summary.specialOrderedItem]);

  const hasItems = summary.orderedItem.length + summary.specialOrderedItem.length === 0;

  return (
    <Container>
      <HeaderContainer isDone={summary.status === HospitalityActionState.Done}>
        <Column>
          <BodyLarge colour={Colours.white} weight={600}>{summary.meetingRoomName} - {summary.officeName}</BodyLarge>
          <BodySmall colour={Colours.white}>{dateToFromString}</BodySmall>
        </Column>

        <AvatarRow>
          <UserAvatarAndName firstName={summary.meetingOwnerUserId.split(' ')[0]}
                             lastName={summary.meetingOwnerUserId.split(' ')[1]}
                             hideName={true}
                             colour={'white'} />
          <BodySmall colour={Colours.white}>{summary.meetingOwnerUserId}</BodySmall>
        </AvatarRow>

      </HeaderContainer>
      {(hasItems) ? <EmptyContainer>
        <BodyLarge>{t('hospitality.no-items')}</BodyLarge>
      </EmptyContainer> : <MenuList>
        <ItemRow>
          <BodySmall weight={600} className={'item__label'}>{t('hospitality.item')}</BodySmall>
          <BodySmall weight={600} className={'item__quantity'}>{t('hospitality.quantity')}</BodySmall>
          <BodySmall weight={600} className={'item__totalCost'}>{t('hospitality.total-cost')}</BodySmall>
        </ItemRow>
        {summary.orderedItem.map((item: any, key: number) => <ItemRow key={key}>
          <BodySmall className={'item__label'} title={item.label}>{item.label}</BodySmall>
          <BodySmall className={'item__quantity'} title={item.quantity}>{item.quantity}</BodySmall>
          <BodySmall className={'item__totalCost'} title={`£${item.totalCost.toFixed(2)}`}>£{item.totalCost.toFixed(2)}</BodySmall>
        </ItemRow>)}
        {summary.specialOrderedItem.map((item: any, key: number) => <ItemRow key={key}>
          <BodySmall className={'item__label'} title={item.label}>{item.label}</BodySmall>
          <BodySmall className={'item__quantity'} title={item.quantity}>{item.quantity}</BodySmall>
          <BodySmall className={'item__totalCost'} title={`£${item.totalCost.toFixed(2)}`}>£{item.totalCost.toFixed(2)}</BodySmall>
        </ItemRow>)}
      </MenuList>}

      <Footer>
        <ItemRow>
          <div className={'item__label'}>
            <HospitalityOrderProgressStateControls bookingSummary={summary} />
            {!hasItems && <BodySmall colour={Colours.veryDarkBlue} weight={600}
                        style={{marginLeft: 'auto', marginRight: '8%'}}>{t('allowance.total')}</BodySmall>}
          </div>
          {!hasItems && <>
            <BodySmall colour={Colours.veryDarkBlue} className={'item__quantity'} weight={600}>{totalQuantity}</BodySmall>
            <BodySmall colour={Colours.veryDarkBlue} className={'item__totalCost'} weight={600}>£{totalCost}</BodySmall>
          </>}
        </ItemRow>
      </Footer>
    </Container>
  )
}

interface Props {
  summary: HospitalityMeetingBookingSummary;
}

const Container = styled.div`
  max-width: 100%;
  width: 850px;
  background-color: ${Colours.white};
  border-radius: 16px;
  margin: 0 auto 16px auto;
`

const HeaderContainer = styled.div<any>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 50px;
  background-color: ${props => props.isDone ? Colours.darkGreen : Colours.veryDarkBlue};
  border-top-right-radius: 16px;
  border-top-left-radius: 16px;
  padding: 8px 32px;
`

const AvatarRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  .userAvatarAndName {
    margin-right: 12px;
  }
`

const EmptyContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 30px 0;
`

const MenuList = styled.div`
  
`

const Footer = styled.div`
  border-radius: 0 0 11px 11px;
  width: 100%;
  margin-top: auto;
  align-self: flex-end;
  background: #0057FF1A;
  display: none;
  @media (${tablet}) {
    display: block;
  }
`

const ItemRow = styled.div`
  display: flex;
  width: 100%;
  padding: 8px 24px;
  border-bottom: 1px solid var(--Neutrals-Light-Grey, #EDEDED);
  &:last-child {
    border-bottom: none;
  }
  .item__label {
    width: 70%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: flex;
    align-items: center;
  }
  .item__quantity {
    width: 15%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: flex;
    align-items: center;
  }
  .item__totalCost {
    width: 15%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: flex;
    align-items: center;
  }
`
